import React from "react"

import { Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CustomerStatus, CustomerType } from "@/types/collections"

import { CustomerDialogState } from "./useCustomerDialog"

const CustomerDialog: React.FC<{ dialog: CustomerDialogState }> = ({ dialog }) => {
  const { open, setOpen, editing, form, setForm, save, isSaving } = dialog
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Customer" : "Add Customer"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update customer details" : "Create a new customer record"}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="grid gap-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={form.name || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className="bg-white"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="accountId">Account ID *</Label>
            <Input
              id="accountId"
              value={form.account_id || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, account_id: e.target.value }))
              }
              className="bg-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="customerType">Customer Type</Label>
              <Select
                value={form.customer_type as string}
                onValueChange={(value: CustomerType) =>
                  setForm((prev) => ({ ...prev, customer_type: value }))
                }
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="shipper">Shipper</SelectItem>
                  <SelectItem value="pudo">PUDO</SelectItem>
                  <SelectItem value="haulage">Haulage</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={form.status as string}
                onValueChange={(value: CustomerStatus) =>
                  setForm((prev) => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={!form.name || !form.account_id || isSaving}
            variant="confirm"
          >
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editing ? "Save Changes" : "Create Customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CustomerDialog
